import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { getTask, getTaskResult } from "../api";
import { reportHistory } from "../services/reportHistory";
import { useUpdateReport } from "./useReportHistory";
import { isMarkdownPlan, parsePlanFromMarkdown } from "../utils/parsePlan";

function getSimplePlan(plan) {
  const simplePlan = [];
  if (!plan || plan.length === 0) {
    return simplePlan;
  }
  for (const item of plan) {
    if (item.slides)
      simplePlan.push(
        ...item.slides.map((s) => ({ title: item?.title, sub_request: s.sub_request }))
      );
  }
  return simplePlan;
}

const pollTaskQueryFn =
  (updateReport) =>
  async ({ queryKey }) => {
    const taskId = queryKey[1];
    const id = queryKey[2];
    const r = await getTask({ task_id: taskId });
    const plan = r?.progress_details?.plan || null;
    let simplePlan = null;
    if (isMarkdownPlan(plan)) {
      simplePlan = parsePlanFromMarkdown(plan);
    } else {
      simplePlan = getSimplePlan(plan);
    }

    if (r.status === "done") {
      const result = await getTaskResult({ task_id: taskId });
      updateReport.mutate({
        id,
        updates: {
          plan,
          simplePlan,
          progress: r.progress,
          progressDetails: r.progress_details,
          taskStatus: r.status,
          status: "complete",
          completedAt: new Date().toISOString(),
          report: result?.output,
        },
      });
    } else if (r.status === "failed") {
      updateReport.mutate({
        id,
        updates: {
          plan,
          simplePlan,
          status: "failed",
          progress: r.progress,
          progressDetails: r.progress_details,
          taskStatus: r.status,
          completedAt: new Date().toISOString(),
        },
      });
    } else {
      updateReport.mutate({
        id,
        updates: {
          plan,
          simplePlan,
          status: "building",
          progress: r.progress,
          progressDetails: r.progress_details,
          taskStatus: r.status,
        },
      });
    }
    return reportHistory.get(id);
  };

function refetchInterval(query) {
  if (query?.state.isError) return false;
  if (!query?.state?.data?.taskId) return false;
  if (["complete", "failed"].includes(query?.state?.data?.status)) return false;
  return 1000;
}

export function usePollTask(id, enabled = true) {
  const { isAuthenticated } = useAuth0();
  const report = reportHistory.get(id);
  const taskId = report?.taskId;
  const updateReport = useUpdateReport();
  return useQuery({
    queryKey: ["task", taskId, id],
    queryFn: pollTaskQueryFn(updateReport),
    enabled: !!(enabled && taskId && id && isAuthenticated),
    refetchInterval,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
}
